import type { CreatorProductInfoRequest } from '@/interface/creatorOrderV2';
import type { PhysicalSamplingConfig, ProductSelectionSnapshot } from './types';
import { ProductOptionSource, SamplingMode } from './types';

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Translate the documented product DTO at the boundary; physicalSampling remains UI-only. */
export const samplingApiToForm = (
  value: unknown
): Record<string, unknown> | null => {
  const product = record(value);
  if (
    product.physicalProductSamplingMethod == null &&
    product.physicalProductOptionMode == null &&
    !Array.isArray(product.physicalProducts)
  )
    return null;
  let isUnsupported =
    product.physicalProductSamplingMethod != null &&
    !['SINGLE', 'MULTIPLE'].includes(
      String(product.physicalProductSamplingMethod)
    );
  const multiple = product.physicalProductSamplingMethod === 'MULTIPLE';
  const mapProduct = (value: unknown, index: number) => {
    const item = record(value);
    const mode =
      item.physicalProductOptionMode ??
      (list(item.skuList).length ? 'MANUAL' : 'NONE');
    if (!['NONE', 'MANUAL', 'PRODUCT_PAGE'].includes(String(mode)))
      isUnsupported = true;
    const guide = record(item.physicalProductOptionGuide);
    return {
      id: multiple
        ? item.productId || `product-${index + 1}`
        : 'legacy-product',
      serverProductId: item.productId,
      name: item.physicalProductName,
      imageUrl: item.physicalProductPhoto,
      imageFileName: item.physicalProductPhotoName,
      productUrl: item.physicalProductLink,
      retailPriceUsd: item.physicalProductCashValue,
      optionRequirement: mode === 'NONE' ? 'NONE' : 'REQUIRED',
      optionSource: mode === 'PRODUCT_PAGE' ? 'PRODUCT_PAGE' : 'MANUAL',
      options:
        mode === 'MANUAL'
          ? list(item.skuList).map((value) => {
              const option = record(value);
              return {
                ...option,
                legacyFreeText: !Array.isArray(option.values)
              };
            })
          : [],
      productPageSelectionGuide: guide.text,
      productPageSelectionGuideFormat: guide.textFormat,
      productPageSelectionGuideImages: guide.images,
      brandName: product.brandName ?? product.name,
      brandLogoUrl:
        product.productLogo ??
        product.logoUrl ??
        (typeof product.logo === 'string'
          ? product.logo
          : record(product.logo).url)
    };
  };
  const products = (multiple ? list(product.physicalProducts) : [product]).map(
    mapProduct
  );
  if (isUnsupported)
    return {
      mode: multiple
        ? SamplingMode.MULTIPLE_PRODUCTS
        : SamplingMode.SINGLE_PRODUCT,
      products: [],
      maxSelectableProducts: 1,
      isUnsupported: true
    };
  return {
    mode: multiple
      ? SamplingMode.MULTIPLE_PRODUCTS
      : SamplingMode.SINGLE_PRODUCT,
    maxSelectableProducts: multiple ? product.physicalProductSelectCount : 1,
    products,
    ...(!multiple &&
    (!product.physicalProductOptionMode || !product.physicalProductLink)
      ? { isLegacy: true }
      : {})
  };
};

/** Returned selections and Address productDetails are frozen service data, never current campaign data. */
export const samplingApiSelection = (
  value: unknown
): Record<string, unknown> | null => {
  const source = record(value);
  const products = Array.isArray(source.selectedProducts)
    ? source.selectedProducts
    : Array.isArray(source.products)
      ? source.products
      : source.optionsOnPageSelection
        ? [{ ...source, productId: 'legacy-product' }]
        : undefined;
  if (!Array.isArray(products)) return null;
  return {
    products: products.map((value, index) => {
      const item = record(value);
      if (item.productSnapshot || item.optionSelections) return item;
      const fields = list(item.fields ?? item.skuList).map((value) => {
        const field = record(value);
        return { optionName: field.label, value: field.value };
      });
      const name = item.physicalProductName ?? item.productName;
      const photo = item.physicalProductPhoto ?? item.productPhoto;
      return {
        productId: item.productId || `selected-product-${index + 1}`,
        productSnapshot:
          name || photo
            ? {
                id: item.productId || `selected-product-${index + 1}`,
                name,
                imageUrl: photo,
                productUrl: item.physicalProductLink ?? item.productLink,
                retailPriceUsd:
                  item.physicalProductCashValue ?? item.productPrice,
                optionRequirement: fields.length ? 'REQUIRED' : 'NONE',
                optionSource: fields.length ? 'MANUAL' : undefined,
                options: list(item.fields ?? item.skuList)
              }
            : undefined,
        optionSelections: fields,
        productPageSelection:
          item.physicalProductOptionsOnPageSelection ??
          item.optionsOnPageSelection
      };
    })
  };
};

/** Only product identity and user choices are writable; snapshots/prices are service-owned. */
export const selectionToApi = (
  config: PhysicalSamplingConfig,
  selection: ProductSelectionSnapshot
): CreatorProductInfoRequest => {
  if (config.isUnsupported)
    throw new Error('Unsupported sampling configuration');
  const toChoices = (item: ProductSelectionSnapshot['products'][number]) =>
    item.optionSelections.map((option) => ({
      label: option.optionName,
      value: option.value
    }));
  if (config.mode === SamplingMode.MULTIPLE_PRODUCTS)
    return {
      selectedProducts: selection.products.map((item) => ({
        productId: item.productId,
        ...(item.optionSelections.length ? { skuList: toChoices(item) } : {}),
        ...(item.productPageSelection
          ? { physicalProductOptionsOnPageSelection: item.productPageSelection }
          : {})
      }))
    };
  if (config.products[0]?.optionSource === ProductOptionSource.PRODUCT_PAGE)
    return {
      physicalProductOptionsOnPageSelection:
        selection.products[0]?.productPageSelection || ''
    };
  return {
    skuList: selection.products[0] ? toChoices(selection.products[0]) : []
  };
};
